import { Button } from "../../ui/button";
import { CheckboxField, SelectField, TextAreaField, TextField } from "../../ui/fields";
import {
  type BuilderFieldType,
  type BuilderRow,
  FIELD_LIMITS,
  keyFromLabel,
  newRow,
  type RowErrors,
} from "../field-builder";
import { fieldTypeOptions } from "../labels";
import { fieldGridClass, hintClass } from "./parts";

// The question field builder (CHT-751): staff add up to 8 typed fields the customer fills in one form. Every control has a visible
// label. A row is a fieldset named "Field n". The remove button names the field it removes. Errors show after a submit attempt
// (`errors`), and the first invalid control takes focus (the dialog calls `focusFirstInvalid`).

type Props = Readonly<{
  rows: readonly BuilderRow[];
  onChange: (rows: readonly BuilderRow[]) => void;
  /** One entry per row, or `undefined` before the first submit attempt. */
  errors: readonly RowErrors[] | undefined;
  idPrefix: string;
  nextRowId: () => number;
}>;

export function FieldBuilder({ rows, onChange, errors, idPrefix, nextRowId }: Props) {
  const full = rows.length >= FIELD_LIMITS.maxFields;

  const update = (rowId: number, patch: Partial<BuilderRow>) =>
    onChange(rows.map((row) => (row.rowId === rowId ? { ...row, ...patch } : row)));

  return (
    <fieldset
      className="m-0 grid min-w-0 gap-4 rounded-md border border-border p-4"
      aria-describedby={`${idPrefix}-limit`}
    >
      <legend className="px-2 text-sm font-semibold">
        Fields for the customer to fill in (optional)
      </legend>
      <p id={`${idPrefix}-limit`} className={hintClass}>
        {rows.length === 0
          ? "With no fields the customer answers in one free-text reply."
          : `${rows.length} of ${FIELD_LIMITS.maxFields} fields.`}{" "}
        The customer sees these labels and nothing else of this request.
      </p>
      {rows.map((row, index) => {
        const base = `${idPrefix}-${row.rowId}`;
        const rowErrors = errors?.[index];
        const number = index + 1;
        return (
          <fieldset
            key={row.rowId}
            className="m-0 grid min-w-0 gap-3 rounded-sm border border-border bg-surface-sunken p-3"
          >
            <legend className="px-2 text-sm font-semibold">Field {number}</legend>
            <div className={fieldGridClass}>
              <TextField
                id={`${base}-label`}
                label={`Label of field ${number}`}
                value={row.label}
                maxLength={FIELD_LIMITS.labelChars}
                required
                error={rowErrors?.label}
                onChange={(label) =>
                  update(row.rowId, {
                    label,
                    ...(row.keyTouched
                      ? {}
                      : { key: label.trim() === "" ? "" : keyFromLabel(label) }),
                  })
                }
              />
              <SelectField
                id={`${base}-type`}
                label={`Type of field ${number}`}
                value={row.type}
                options={fieldTypeOptions}
                required
                onChange={(type) => update(row.rowId, { type: type as BuilderFieldType })}
              />
              <TextField
                id={`${base}-key`}
                label={`Key of field ${number}`}
                hint="The name of the answer. Made from the label unless you change it."
                value={row.key}
                maxLength={FIELD_LIMITS.keyChars}
                required
                error={rowErrors?.key}
                onChange={(key) => update(row.rowId, { key, keyTouched: true })}
              />
              <CheckboxField
                id={`${base}-required`}
                label={`Field ${number} is required`}
                checked={row.required}
                onChange={(required) => update(row.rowId, { required })}
              />
            </div>
            {row.type === "CHOICE" ? (
              <TextAreaField
                id={`${base}-options`}
                label={`Options of field ${number}`}
                hint={`One option per line, ${FIELD_LIMITS.minOptions} to ${FIELD_LIMITS.maxOptions} options.`}
                value={row.optionsText}
                rows={4}
                required
                error={rowErrors?.options}
                onChange={(optionsText) => update(row.rowId, { optionsText })}
              />
            ) : null}
            {/* The name is "Remove field <n>: <label>". The label is text the staff member wrote for the customer, so it is page text inside the
                button (hidden from the eye), never an attribute. */}
            <Button
              variant="outline"
              className="justify-self-start"
              onClick={() => onChange(rows.filter((other) => other.rowId !== row.rowId))}
            >
              Remove field{" "}
              <span className="sr-only">
                {number}
                {row.label.trim() === "" ? "" : `: ${row.label.trim()}`}
              </span>
            </Button>
          </fieldset>
        );
      })}
      <Button
        variant="outline"
        className="justify-self-start"
        disabled={full}
        aria-describedby={full ? `${idPrefix}-full` : undefined}
        onClick={() => onChange([...rows, newRow(nextRowId())])}
      >
        Add a field
      </Button>
      {full ? (
        <p id={`${idPrefix}-full`} className={hintClass}>
          The limit is {FIELD_LIMITS.maxFields} fields.
        </p>
      ) : null}
    </fieldset>
  );
}
